import InfoTip from '../../components/InfoTip';
import React, { FC } from 'react';
import {
  Alert,
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { tokens } from '../../theme';
import { DesignAbData, DesignAbRow } from '../../helpers/webinarStatisticsData';
import { formatNumber } from '../../helpers/numberUtils';
import { formatPct } from '../../helpers/kpiDefinitions';

interface DesignAbTabProps {
  colors: ReturnType<typeof tokens>;
  data: DesignAbData | null;
}

/** "—" statt 0, wo der Nenner fehlt oder gesperrt ist. Eine fehlende Rate ist keine Null. */
const strich = (v: number | null | undefined, digits = 0): string =>
  v === null || v === undefined ? '—' : formatNumber(v, digits);

const prozent = (v: number | null | undefined): string =>
  v === null || v === undefined ? '—' : formatPct(v * 100, 1);

const ARM_NAME: Record<string, string> = { A: 'A · electric', B: 'B · bp' };

const DesignAbTab: FC<DesignAbTabProps> = ({ colors, data }) => {
  if (!data) return null;
  const { rows, meta } = data;

  const head = {
    color: 'black',
    backgroundColor: '#e0e0e0 !important',
    fontWeight: 'bold',
  } as const;

  const zeile = (r: DesignAbRow) => {
    const [a, b] = r.arme;
    return (
      <React.Fragment key={r.webinar_id}>
        <TableRow>
          <TableCell rowSpan={2} sx={{ verticalAlign: 'top' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {r.title || r.webinar_id}
            </Typography>
            {/* display:block — als Inline-Span legen sich die Chips darunter ueber das Datum
                (gemessen 2026-08-31 bei FviSD8/gBFUAq: die Kennung war zur Haelfte verdeckt). */}
            <Typography variant="caption" sx={{ color: colors.grey[300], display: 'block' }}>
              {r.webinar_id} · {String(r.start_date).slice(0, 10)}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {!r.nenner_vollstaendig && (
                // Zahlen bleiben stehen, die Rate nicht — und die ZAHL der betroffenen Anmeldungen
                // steht dabei, weil 1 von 200 anders zu beurteilen ist als 13 von 14.
                <Tooltip
                  title={`An ${r.tage_ohne_auslieferungsdaten} Tag(en) mit Anmeldungen fehlen Auslieferungsdaten. Betroffen: ${r.unabgedeckte_anmeldungen} Anmeldung(en). Ohne vollständigen Nenner wäre jede Quote zu hoch.`}
                >
                  <Chip
                    label={`Nenner unvollständig (${r.unabgedeckte_anmeldungen})`}
                    size="small"
                    color="warning"
                  />
                </Tooltip>
              )}
              {r.kreuz_adressen > 0 && (
                <Tooltip title="Diese Adressen sind in BEIDEN Armen aufgetaucht (zweites Gerät, Cookie läuft auf Path=/). Sie sind aus beiden Armen herausgenommen.">
                  <Chip label={`${r.kreuz_adressen} in beiden Armen`} size="small" />
                </Tooltip>
              )}
            </Box>
          </TableCell>
          {armZellen(a, r)}
        </TableRow>
        <TableRow>{armZellen(b, r)}</TableRow>
        <TableRow>
          <TableCell colSpan={7} sx={{ backgroundColor: colors.primary[400], py: 1 }}>
            <Typography variant="caption" sx={{ color: colors.grey[200] }}>
              <strong>Show-up:</strong> {r.showup_vergleich.urteil}
              {' · '}
              <strong>Balance:</strong> {prozent(r.balance.anteilA)} /{' '}
              {prozent(r.balance.anteilA === null ? null : 1 - r.balance.anteilA)}
              {r.balance.auffaellig && (
                <span style={{ color: '#f57c00' }}>
                  {' '}
                  — auffällige Schieflage ({strich(r.balance.abweichungSd, 1)} SD von 50/50)
                </span>
              )}
            </Typography>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  const armZellen = (arm: DesignAbRow['arme'][number], r: DesignAbRow) => (
    <>
      <TableCell sx={{ fontWeight: 'bold' }}>{ARM_NAME[arm.arm] ?? arm.arm}</TableCell>
      <TableCell align="right">{strich(arm.auslieferungen)}</TableCell>
      <TableCell align="right">{strich(arm.anmelder)}</TableCell>
      <TableCell align="right">
        {arm.anmelder_je_1000 === null ? (
          <Tooltip title="Gesperrt, solange der Nenner Lücken hat.">
            <span>—</span>
          </Tooltip>
        ) : (
          formatNumber(arm.anmelder_je_1000, 0)
        )}
      </TableCell>
      <TableCell align="right">{strich(arm.teilgenommen)}</TableCell>
      <TableCell align="right">
        {prozent(arm.showup.anteil)}
        {arm.showup.unten !== null && (
          <Typography variant="caption" sx={{ display: 'block', color: colors.grey[300] }}>
            {prozent(arm.showup.unten)} – {prozent(arm.showup.oben)}
          </Typography>
        )}
      </TableCell>
    </>
  );

  const cron = meta.zaehler_cron;

  return (
    // minWidth: 0 ist das Entscheidende: ohne das weigert sich der Flex-Kandidat, unter seine
    // min-content-Breite zu schrumpfen, und die Tabelle schiebt die GANZE Seite horizontal auf.
    <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
      <Typography variant="h6" sx={{ color: colors.grey[100], mb: 1 }}>
        Design-A/B der Anmeldeseiten
      </Typography>

      {/* Ein roter Zaehler-Cron heisst: die Nenner unten sind veraltet. Am 31.08. stand er drei
          Tage auf failure, und ohne diesen Banner haette der Tab "wenig Traffic" gezeigt. */}
      {cron && !cron.gesund && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Der Auslieferungszähler läuft nicht sauber ({cron.fehler_in_folge} Fehler in Folge,
          zuletzt {cron.zuletzt ? String(cron.zuletzt).slice(0, 16).replace('T', ' ') : 'nie'}). Die
          Auslieferungszahlen unten sind möglicherweise veraltet.
          {cron.fehler ? ` — ${cron.fehler}` : ''}
        </Alert>
      )}
      {!meta.tz_tabellen_geladen && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Die Zeitzonentabellen der Datenbank fehlen — der Abdeckungs-Riegel sperrt deshalb jede
          Rate. Das ist die sichere Richtung, aber kein Datenproblem.
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        {meta.hinweis}
        {meta.log_beginnt
          ? ` Auslieferungsdaten liegen ab ${meta.log_beginnt} vor; für Webinare davor bleibt die Rate gesperrt.`
          : ''}
      </Alert>

      {rows.length === 0 ? (
        <Typography sx={{ color: colors.grey[300] }}>
          Keine Webinare mit Design-A/B im gewaehlten Zeitraum.
        </Typography>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={head}>Webinar</TableCell>
                <TableCell sx={head}>
                  Arm
                  <InfoTip text="Welche Design-Variante der Anmeldeseite die Person gesehen hat." />
                </TableCell>
                <TableCell sx={head} align="right">
                  Auslieferungen
                  <InfoTip text="Wie oft die Anmeldeseite in dieser Variante ausgeliefert wurde (Seitenaufrufe, nicht Personen)." />
                </TableCell>
                <TableCell sx={head} align="right">
                  Anmelder
                  <InfoTip text="Personen, die sich über diese Variante angemeldet haben (Variante der ersten Anmeldung)." />
                </TableCell>
                <TableCell sx={head} align="right">
                  <Tooltip title="Anmelder je 1.000 Auslieferungen. Der Nenner zählt Seitenaufrufe, nicht Personen — Diagnose, keine Conversion-Rate.">
                    <span>je 1.000</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={head} align="right">
                  Teilnehmer
                  <InfoTip text="Davon live im Webinar dabei." />
                </TableCell>
                <TableCell sx={head} align="right">
                  <Tooltip title="Live-Teilnahme nach derselben Definition wie /webinar-auswertungen (Teilnehmer ÷ rechtzeitig Angemeldete), mit 95-%-Intervall.">
                    <span>Show-up</span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{rows.map(zeile)}</TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default DesignAbTab;
